import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ChevronRight, ChevronDown } from "lucide-react";
import { Container, Section, SectionHeading, Reveal, LeafBackdrop } from "@/components/common";
import { Icon, PageHero, CTABand } from "@/components/ui-extras";
import { CAPABILITIES_ACCORDION, TRADE_PROCESS, IMG, img } from "@/data/content";

const TradeCapabilities = () => {
  const [open, setOpen] = useState(0);
  const [active, setActive] = useState(0);
  const activeStep = TRADE_PROCESS[active];

  return (
    <div>
      <PageHero
        eyebrow="Trade Capabilities"
        title="Structured Agricultural Export, Executed with Discipline"
        intro="From sourcing and grading to packaging, documentation, and shipment coordination — Prince Trading Impex supports international buyers through one dependable export framework. Click each capability to explore."
        image={img(IMG.documents, 1600)}
      />

      {/* Accordion */}
      <Section className="bg-white">
        <Container>
          <SectionHeading center eyebrow="Operational Strength" title="What We Do, Step by Step" className="mb-12" />
          <div className="mx-auto max-w-3xl space-y-3" data-testid="capabilities-accordion">
            {CAPABILITIES_ACCORDION.map((c, i) => {
              const isOpen = open === i;
              return (
                <Reveal key={c.title} delay={(i % 4) * 0.04}>
                  <div
                    data-testid="capability-item"
                    className="rounded-[var(--radius)] overflow-hidden transition-colors"
                    style={{ border: isOpen ? "1px solid var(--pt-border-gold)" : "1px solid var(--pt-border)", background: isOpen ? "var(--pt-paper)" : "#fff", boxShadow: isOpen ? "var(--shadow-soft)" : "none" }}
                  >
                    <button
                      data-testid="capability-toggle"
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      className="flex w-full items-center gap-4 px-5 sm:px-6 py-5 text-left"
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full" style={{ background: "rgba(58,187,176,0.10)", border: "1px solid var(--pt-border-gold)" }}>
                        <Icon name={c.icon} size={20} color="#0E8C82" />
                      </span>
                      <span className="flex-1 font-serif text-xl sm:text-2xl" style={{ color: "var(--pt-navy)" }}>{c.title}</span>
                      <span className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: isOpen ? "linear-gradient(90deg,#3ABBB0,#41E19A)" : "rgba(58,187,176,0.1)", color: isOpen ? "#020606" : "#0E8C82" }}>
                        {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="px-5 sm:px-6 pb-6 pl-[4.75rem]">
                            <ul className="space-y-2.5">
                              {c.lines.map((line, idx) => (
                                <li key={idx} className="flex gap-3 font-sans text-sm leading-relaxed" style={{ color: "var(--pt-muted)" }}>
                                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: "#3ABBB0" }} />
                                  {line}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </Section>

      {/* Trade process — interactive flowchart */}
      <section className="relative overflow-hidden hero-navy-gradient">
        <div className="gold-hairline" />
        <LeafBackdrop size={440} className="-left-28 top-16 opacity-15" />
        <LeafBackdrop size={360} className="-right-24 bottom-10 opacity-15" color="rgba(65,225,154,0.06)" />
        <Container wide className="relative py-16 sm:py-20 lg:py-24">
          <SectionHeading onNavy center eyebrow="Trade Process" title="From Buyer Inquiry to Long-Term Supply" intro="A disciplined, transparent process that gives buyers confidence at every stage. Hover or tap a stage to see how it works." className="mb-14" />

          <div data-testid="trade-process-flow" className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:gap-x-8 lg:gap-y-12">
            {TRADE_PROCESS.map((s, i) => {
              const isActive = active === i;
              const rowEnd = i % 4 === 3;
              const isLast = i === TRADE_PROCESS.length - 1;
              return (
                <div key={s.step} className="relative flex flex-col">
                  <button
                    data-testid="trade-process-node"
                    onMouseEnter={() => setActive(i)}
                    onFocus={() => setActive(i)}
                    onClick={() => setActive(i)}
                    aria-pressed={isActive}
                    aria-label={`Step ${s.step}: ${s.title}`}
                    className="group flex h-full w-full flex-col items-start gap-3 rounded-[var(--radius)] p-5 text-left transition-transform duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3ABBB0]"
                    style={{
                      background: isActive ? "linear-gradient(150deg, rgba(58,187,176,0.18), rgba(2,6,6,0.20))" : "rgba(255,255,255,0.04)",
                      border: isActive ? "1px solid #3ABBB0" : "1px solid rgba(58,187,176,0.22)",
                      boxShadow: isActive ? "0 0 26px rgba(58,187,176,0.40), inset 0 0 20px rgba(65,225,154,0.08)" : "none",
                      transform: isActive ? "translateY(-3px)" : "none",
                    }}
                  >
                    <div className="flex w-full items-center justify-between">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full font-sans text-sm font-bold" style={{ background: "linear-gradient(135deg,#3ABBB0,#41E19A)", color: "#020606" }}>{s.step}</span>
                      <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ background: isActive ? "rgba(65,225,154,0.14)" : "rgba(58,187,176,0.08)", border: "1px solid rgba(58,187,176,0.3)" }}>
                        <Icon name={s.icon} size={20} color={isActive ? "#41E19A" : "#3ABBB0"} />
                      </span>
                    </div>
                    <h3 className="font-serif text-lg leading-snug text-white">{s.title}</h3>
                  </button>

                  {/* horizontal connector (desktop) */}
                  {!rowEnd && !isLast && (
                    <span aria-hidden="true" className="absolute right-[-22px] top-[42px] z-10 hidden lg:block">
                      <ChevronRight size={24} color="rgba(58,187,176,0.65)" />
                    </span>
                  )}
                  {/* vertical connector (tablet / mobile) */}
                  {!isLast && (
                    <span aria-hidden="true" className="flex justify-center py-1.5 lg:hidden">
                      <ChevronDown size={20} color="rgba(58,187,176,0.55)" />
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Detail panel for the selected step */}
          <div className="mx-auto mt-10 max-w-3xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                data-testid="trade-process-detail"
                className="rounded-[var(--radius)] p-6 sm:p-7"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid #3ABBB0", boxShadow: "0 0 24px rgba(58,187,176,0.18)" }}
              >
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full" style={{ background: "rgba(58,187,176,0.14)", border: "1px solid #3ABBB0", boxShadow: "0 0 14px rgba(65,225,154,0.25)" }}>
                    <Icon name={activeStep.icon} size={22} color="#41E19A" />
                  </span>
                  <div>
                    <span className="font-sans text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: "#36B4B2" }}>Step {activeStep.step}</span>
                    <h4 className="font-serif text-2xl leading-tight text-white">{activeStep.title}</h4>
                  </div>
                </div>
                <p className="mt-4 font-sans text-base leading-relaxed" style={{ color: "var(--pt-muted-on-navy)" }}>{activeStep.text}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </Container>
        <div className="gold-hairline" />
      </section>

      <CTABand
        heading="Plan your next commodity order with a trusted partner."
        subtext="Tell us your product, quantity, and destination — email our team and we'll guide the rest."
      />
    </div>
  );
};

export default TradeCapabilities;
